import ResponderBaseModal from 'features/Responder/components/ResponderBaseModal';
import ResponderBaseTable from 'features/Responder/components/ResponderBaseTable';
import useResponderDialog from 'features/Responder/hooks/useResponderDialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import './Modal.css';

const stringifyId = (id) => `${id}`;

const toStringIdSet = (ids) => new Set((Array.isArray(ids) ? ids : []).map(stringifyId));

const areSetsEqual = (left, right) => {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
};

const Modal = ({
  show,
  handleShow,
  members = [],
  loading = false,
  saving = false,
  error = '',
  onRefresh,
  selectedPersonnelIds = [],
  onSavePersonnel = async () => true,
}) => {
  const [draftSelectedPersonnelIds, setDraftSelectedPersonnelIds] = useState([]);
  const { confirm: showConfirm, dialogElement } = useResponderDialog();

  useEffect(() => {
    if (!show) return;
    setDraftSelectedPersonnelIds((Array.isArray(selectedPersonnelIds) ? selectedPersonnelIds : []).map(stringifyId));
  }, [show, selectedPersonnelIds]);

  const rows = useMemo(
    () =>
      members.map((member) => ({
        id: `${member?.userId ?? member?.id ?? member?.email ?? ''}`,
        userId: member?.userId ?? member?.id ?? '-',
        name: member?.name || '-',
        email: member?.email || '-',
      })),
    [members],
  );

  const selectedLookup = useMemo(
    () => toStringIdSet(draftSelectedPersonnelIds),
    [draftSelectedPersonnelIds],
  );
  const savedPersonnelSelectionSet = useMemo(() => toStringIdSet(selectedPersonnelIds), [selectedPersonnelIds]);
  const hasUnsavedPersonnelChanges = useMemo(
    () => !areSetsEqual(selectedLookup, savedPersonnelSelectionSet),
    [savedPersonnelSelectionSet, selectedLookup],
  );

  useEffect(() => {
    if (!show || !hasUnsavedPersonnelChanges) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedPersonnelChanges, show]);

  const allSelected = rows.length > 0 && rows.every((row) => selectedLookup.has(stringifyId(row.id)));

  const toggleSelection = useCallback((id) => {
    const normalizedId = stringifyId(id);
    setDraftSelectedPersonnelIds((prev) => {
      const next = toStringIdSet(prev);
      if (next.has(normalizedId)) {
        next.delete(normalizedId);
      } else {
        next.add(normalizedId);
      }
      return Array.from(next);
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setDraftSelectedPersonnelIds([]);
      return;
    }
    setDraftSelectedPersonnelIds(rows.map((row) => stringifyId(row.id)));
  }, [allSelected, rows]);

  const resetDraftPersonnelToSaved = useCallback(() => {
    setDraftSelectedPersonnelIds((Array.isArray(selectedPersonnelIds) ? selectedPersonnelIds : []).map(stringifyId));
  }, [selectedPersonnelIds]);

  const handleDiscard = useCallback(async () => {
    if (hasUnsavedPersonnelChanges) {
      const shouldDiscard = await showConfirm({
        title: 'Discard personnel changes',
        message: 'You have unsaved changes. Discarding will remove these changes. Continue?',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      });
      if (!shouldDiscard) return;
    }
    resetDraftPersonnelToSaved();
    handleShow?.();
  }, [handleShow, hasUnsavedPersonnelChanges, resetDraftPersonnelToSaved, showConfirm]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    const didSave = await onSavePersonnel(draftSelectedPersonnelIds);
    if (didSave === false) return;
    handleShow?.();
  }, [draftSelectedPersonnelIds, handleShow, onSavePersonnel, saving]);

  const handleRefresh = useCallback(async () => {
    if (saving) return;
    if (hasUnsavedPersonnelChanges) {
      const shouldDiscard = await showConfirm({
        title: 'Discard personnel changes',
        message:
          'You have unsaved changes. Refreshing personnel will discard these changes. Continue?',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      });
      if (!shouldDiscard) return;
      resetDraftPersonnelToSaved();
    }
    onRefresh?.();
  }, [hasUnsavedPersonnelChanges, onRefresh, resetDraftPersonnelToSaved, saving, showConfirm]);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: (
          <Form.Check
            type="checkbox"
            className="personnel-assignment-checkbox"
            checked={allSelected}
            onChange={toggleAll}
            onClick={(event) => event.stopPropagation()}
            aria-label="Select all members"
          />
        ),
        size: 56,
        enableSorting: false,
        Cell: ({ row }) => (
          <Form.Check
            type="checkbox"
            className="personnel-assignment-checkbox"
            checked={selectedLookup.has(stringifyId(row.original.id))}
            onChange={() => toggleSelection(row.original.id)}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select ${row.original.name || 'member'}`}
          />
        ),
      },
      {
        header: 'Name',
        accessorKey: 'name',
        size: 220,
        Cell: ({ cell }) => <span className="personnel-assignment-cell">{cell.getValue()}</span>,
      },
      {
        header: 'Email',
        accessorKey: 'email',
        size: 320,
        Cell: ({ cell }) => <span className="personnel-assignment-cell">{cell.getValue()}</span>,
      },
    ],
    [allSelected, selectedLookup, toggleAll, toggleSelection],
  );

  const footer = (
    <div className="w-100 d-flex justify-content-end gap-2">
      <Button type="button" variant="danger" onClick={handleDiscard}>
        Discard
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={handleSave}
        disabled={loading || saving || !hasUnsavedPersonnelChanges}
      >
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );

  return (
    <>
      <ResponderBaseModal
        show={show}
        onHide={handleDiscard}
        title="Add Personnel"
        size="lg"
        className="personnel-assignments-modal"
        footer={footer}
      >
        <div className="personnel-assignments-toolbar">
          <div className="personnel-assignments-meta">
            Members: <strong>{rows.length}</strong> | Selected:{' '}
            <strong>{draftSelectedPersonnelIds.length}</strong>
          </div>
          <Button
            type="button"
            size="sm"
              variant="outline-secondary"
              className="responder-interactive-action"
            onClick={handleRefresh}
            disabled={loading || saving}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {error && <div className="text-danger small mb-2">{error}</div>}

        {loading && !rows.length ? (
          <div className="d-flex align-items-center gap-2 text-theme">
            <Spinner animation="border" size="sm" />
            <span className="small">Loading members...</span>
          </div>
        ) : rows.length ? (
          <div className="personnel-assignments-table-wrap">
            <ResponderBaseTable
              rows={rows}
              columns={columns}
              onRowClick={(row) => toggleSelection(row.id)}
              selectedRowIds={draftSelectedPersonnelIds}
              selectedRowColor="var(--color-primary-highlight)"
              enableRowVirtualization={false}
              customStyles={{
                container: {
                  backgroundColor: 'var(--color-base)',
                },
                header: {
                  borderBottom: '1px solid var(--color-divider)',
                  fontSize: '0.82rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--color-font)',
                },
                cell: {
                  borderBottom: '1px solid var(--color-divider)',
                  color: 'var(--color-font)',
                  fontSize: '0.95rem',
                  padding: '8px',
                },
                row: {
                  '&:hover': {
                    backgroundColor:
                      'color-mix(in srgb, var(--color-primary-highlight) 12%, var(--color-base))',
                  },
                },
              }}
              muiTableContainerProps={{
                sx: {
                  maxHeight: '46vh',
                  overflowY: 'auto',
                },
              }}
              customEmptyMessage="No members returned."
            />
          </div>
        ) : (
          <div className="small text-theme-muted">No members returned.</div>
        )}
      </ResponderBaseModal>
      {dialogElement}
    </>
  );
};

export default Modal;
